import React, { useState, useEffect } from 'react';
import { WithStyles } from '@material-ui/core/styles';
import { ClassNameMap } from '@material-ui/core/styles/withStyles';
import { withRouter, RouteComponentProps } from 'react-router-dom';

import { useQuery } from '@apollo/client';

import { Grid, withStyles } from '@material-ui/core';

import Dashboard from '../../../../layouts/Dashboard';
import UsersList from '../../../../components/Global/Users/List';
import CustomSortableTable, { SortableTableHeader, SortableTableRow } from '../../../../components/CustomSortableTable';
import CustomViewToggle, { ViewType } from '../../../../components/CustomViewToggle';
import { selectOrganisationUsersInitQuery, OrganisationUser } from './queries';

import styles from './styles';

interface Props extends WithStyles<typeof styles>, RouteComponentProps {
  classes: ClassNameMap<string>;
}

const ListOrganisationUsers = ({ classes, history }: Props): React.ReactElement => {
  const [users, setUsers] = useState<OrganisationUser[]>([]);
  const [viewType, setViewType] = useState<ViewType>(ViewType.TABLE);

  const { data: organisationUserInitQuery, loading } = useQuery(selectOrganisationUsersInitQuery(), { fetchPolicy: 'no-cache' });

  useEffect(() => {
    let mounted = true;
    if (mounted && organisationUserInitQuery) {
      setUsers(organisationUserInitQuery.users_organisation);
    }
    return () => {
      mounted = false;
    };
  }, [organisationUserInitQuery]);

  const handleCreate = () => {
    history.push(`/org/users/create`);
  };

  const handleView = (id: string) => {
    history.push(`/org/users/${id}`);
  };

  const handleEdit = (id: string) => {
    history.push(`/org/users/${id}/edit`);
  };

  const tableHeaders: SortableTableHeader[] = [
    { key: 'full_name', label: 'Name' },
    { key: 'email', label: 'Email' },
  ];

  const tableRows: SortableTableRow[] = users.map((user) => ({
    key: user.id,
    columns: [
      { key: 'full_name', label: user.full_name },
      { key: 'email', label: user.email },
    ],
    actions: [
      { label: 'View', onClick: () => handleView(user.id) },
      { label: 'Edit', onClick: () => handleEdit(user.id) },
    ],
  }));

  return (
    <Dashboard breadcrumbs={[{ label: 'Users' }]}>
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <CustomViewToggle value={viewType} handleChangeView={setViewType} />
        </Grid>
        {viewType === ViewType.TABLE && (
          <Grid item xs={12}>
            <CustomSortableTable
              title="Users"
              counter={users.length}
              headers={tableHeaders}
              rows={tableRows}
              loading={loading}
              searchable={['full_name', 'email']}
              actions={[{ key: 'create', label: 'Create', onClick: () => handleCreate() }]}
            />
          </Grid>
        )}
        {viewType === ViewType.GRID && (
          <Grid item xs={12}>
            <UsersList
              users={users.map((i) => ({ id: i.id, full_name: i.full_name, email: i.email }))}
              gridSize={3}
              handleCreate={handleCreate}
              handleEdit={handleEdit}
              handleView={handleView}
            />
          </Grid>
        )}
      </Grid>
    </Dashboard>
  );
};

export default withRouter(withStyles(styles)(ListOrganisationUsers));
